/**
 * Order times on the restaurant's own clock.
 *
 * Every order carries restaurant.timezone (an IANA name such as
 * "America/Chicago"; the backend never sends it empty). A time is shown in that
 * zone with its abbreviation — "12:30 PM CST" — so a Central restaurant's
 * orders read the same to its owner in Dallas and to an admin in Boston.
 */

export const DEFAULT_TZ = 'America/New_York'

const valid = new Map()
function isValidZone(tz) {
  if (!tz || typeof tz !== 'string') return false
  if (!valid.has(tz)) {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: tz })
      valid.set(tz, true)
    } catch (e) {
      valid.set(tz, false)
    }
  }
  return valid.get(tz)
}

/** A usable zone: `tz` if real, else the platform default (Eastern). */
export const zoneOrDefault = tz => (isValidZone(tz) ? tz : DEFAULT_TZ)

/** The zone an order's times are shown in. */
export const orderZone = order => zoneOrDefault(order?.restaurant?.timezone || order?.timezone)

const toDate = v => {
  if (v == null || v === '') return null
  const d = v instanceof Date ? v : new Date(/^\d+$/.test(String(v)) ? Number(v) : v)
  return Number.isNaN(d.getTime()) ? null : d
}

const fmt = (v, tz, opts) => {
  const d = toDate(v)
  return d ? new Intl.DateTimeFormat('en-US', { ...opts, timeZone: zoneOrDefault(tz) }).format(d) : ''
}

/** "12:30 PM CST" */
export const timeIn = (v, tz) => fmt(v, tz, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })

/** "Nov 3, 2026" — the restaurant's calendar date. */
export const dateIn = (v, tz) => fmt(v, tz, { month: 'short', day: 'numeric', year: 'numeric' })

/** "Tue, Nov 3, 12:30 PM CST" */
export const dateTimeIn = (v, tz) =>
  fmt(v, tz, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })

/** "Nov 3, 2026, 12:30 PM CST" */
export const fullDateTimeIn = (v, tz) =>
  fmt(v, tz, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })

/** The restaurant's calendar date of an instant, as YYYY-MM-DD ('' if not a date). */
export function dayIn(v, tz) {
  const d = toDate(v)
  if (!d) return ''
  const p = {}
  for (const part of new Intl.DateTimeFormat('en-US', { timeZone: zoneOrDefault(tz), year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d)) p[part.type] = part.value
  return `${p.year}-${p.month}-${p.day}`
}

/** Today's date on the restaurant's clock, as YYYY-MM-DD. */
export const todayIn = (tz, now = new Date()) => dayIn(now, tz)

/** '2026-11-03' + 1 → '2026-11-04' (calendar arithmetic, no zone involved). */
export function addDays(dateStr, days) {
  const [y, m, d] = dateStr.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + days)
  return dt.toISOString().slice(0, 10)
}

/**
 * Does an instant fall in a date filter, judged on the restaurant's calendar?
 * filter: TODAY | YESTERDAY | LAST_WEEK | THIS_MONTH | CUSTOM | ALL.
 * range ({ from, to } as YYYY-MM-DD) is read only for CUSTOM; either end may be empty.
 */
export function inDateFilter(v, tz, filter, range, now = new Date()) {
  if (filter === 'ALL' || !filter) return true
  const day = dayIn(v, tz)
  if (!day) return false
  if (filter === 'CUSTOM') {
    let from = range?.from || ''
    let to = range?.to || ''
    if (from && to && from > to) [from, to] = [to, from]
    return (!from || day >= from) && (!to || day <= to)
  }
  const today = todayIn(tz, now)
  switch (filter) {
    case 'TODAY': return day >= today
    case 'YESTERDAY': return day === addDays(today, -1)
    case 'LAST_WEEK': return day >= addDays(today, -7)
    case 'THIS_MONTH': return day >= `${today.slice(0, 7)}-01`
    default: return true
  }
}
